import { $httpFhirApi } from '@/common/api/httpFhir.service';
import organizationApi from '@/common/api/organization.api';
import patientApi from '@/common/api/patient.api';
import practitionerApi from '@/common/api/practitioner.api';
import relatedPersonApi from '@/common/api/relatedPerson.api';
import { useAuthStore } from '@/stores/auth';
import { sortBy } from 'lodash';

export const resourcePriority = {
    Patient: 1,
    Organization: 2,
    Practitioner: 3,
    User: 4,
    RelatedPerson: 5,
};

export default {
    search(queryString, resourcesToSearch = ['patient', 'organization', 'practitioner'], filterByBrand, enforceAccess = true) {
        const authStore = useAuthStore();
        let entry = [];

        let patientIndex, organizationIndex, practitionerIndex, relatedPersonIndex;

        const customSearchKeys = ['brandUsers', 'locationUsers', 'recent'];
        if (customSearchKeys.includes(queryString)) {
            // custom search based on a search identifier
            if (queryString === 'locationUsers') {
                entry.push({
                    request: {
                        method: 'GET',
                        url: practitionerApi.getLocationUsersQuery(filterByBrand?.location || authStore.brand.location),
                    },
                });
            } else if (queryString === 'brandUsers') {
                entry.push({
                    request: {
                        method: 'GET',
                        url: practitionerApi.getBrandUsersQuery(filterByBrand?.brand || authStore.brand),
                    },
                });
            } else if (queryString === 'recent') {
                const query =
                    'BN_List?type=contactAccessLog&owner=' +
                    authStore.practitioner.id +
                    '&_include=BN_List:itemReference:Practitioner' +
                    '&_include=BN_List:itemReference:Organization' +
                    '&_include=BN_List:itemReference:Patient';
                entry.push({
                    request: {
                        method: 'GET',
                        url: query,
                    },
                });
            }
        } else {
            // regular search
            if (resourcesToSearch.includes('patient')) {
                entry.push({
                    request: {
                        method: 'GET',
                        url: patientApi.getTransactionQuery(queryString),
                    },
                });
                patientIndex = entry.length - 1;
            }
            if (resourcesToSearch.includes('organization')) {
                entry.push({
                    request: {
                        method: 'GET',
                        url: organizationApi.getTransactionQuery(queryString),
                    },
                });
                organizationIndex = entry.length - 1;
            }
            if (resourcesToSearch.includes('practitioner')) {
                entry.push({
                    request: {
                        method: 'GET',
                        url: practitionerApi.getTransactionQuery(queryString, filterByBrand),
                    },
                });
                practitionerIndex = entry.length - 1;
            }
            if (resourcesToSearch.includes('relatedperson')) {
                entry.push({
                    request: {
                        method: 'GET',
                        url: relatedPersonApi.getTransactionQuery(queryString),
                    },
                });
                relatedPersonIndex = entry.length - 1;
            }
        }

        let returnPromise = $httpFhirApi.post('/', {
            type: 'transaction',
            resourceType: 'Bundle',
            entry: entry,
        });

        return Promise.resolve(returnPromise).then(async (values) => {
            let returnResults = [];

            // RECENT CONTACTS
            if ('recent' === queryString) {
                let contacts = [];
                const recent = values.data[0].entry;
                const accessLog = recent.find((item) => item.resourceType === 'BN_List');
                if (!accessLog) return contacts;

                // Get the ids from the origianlData array, they are being sorted and not appearing the way there were used
                let orgDataBnListItems = values?.originalData?.entry[0]?.resource?.entry.find((item) => item.resource.resourceType === 'BN_List');
                let bnList = orgDataBnListItems?.resource.item.map((item) => item.value.Reference.id);
                // Loop thru the item ids and build the contacts array with the FHIRObject found in the recent array
                bnList.forEach((itemId) => {
                    let fhirObj = recent.find((item) => item.id === itemId);
                    if (fhirObj) contacts.push(fhirObj);
                });

                return patientApi.processPatientResponse(contacts, enforceAccess);
            }

            if (['locationUsers', 'brandUsers'].includes(queryString)) {
                returnResults = returnResults.concat(practitionerApi.processBrandUsersResponse(values.data[0].entry));
                return sortBy(returnResults, ['fullName']);
            }

            if (resourcesToSearch.includes('organization')) {
                returnResults = returnResults.concat(organizationApi.processOrganizationResponse(values.data[organizationIndex].entry));
            }

            if (resourcesToSearch.includes('practitioner')) {
                returnResults = returnResults.concat(practitionerApi.processPractitionerResponse(values.data[practitionerIndex].entry));
            }

            if (resourcesToSearch.includes('relatedperson')) {
                returnResults = returnResults.concat(await relatedPersonApi.processRelatedPersonResponse(values.data[relatedPersonIndex].entry));
            }

            // Patient is last because it's an asyncronous function that runs 2 queries.  So we can process returned data while Patient is still querying
            if (resourcesToSearch.includes('patient')) {
                returnResults = returnResults.concat(await patientApi.processPatientResponse(values.data[patientIndex].entry, enforceAccess));
            }

            return sortBy(returnResults, ['_group', (o) => resourcePriority[o.resourceType], 'fullName']);
        });
    },
};
